/**
 * RolloverTip — the hover information tooltip (Faz 11.12, ROLLOVERTIPS
 * sysvar parity): with the sysvar on (default off — AutoCAD ships it off),
 * hovering an entity for 500ms shows a mini tooltip next to the cursor with
 * the entity type, layer and name (max 2 lines).
 *
 * Hidden while the dynamic-input balloon owns the cursor area (a typed
 * buffer or a running command prompt) so the two never collide. Pure DOM,
 * pointer-transparent, mounted once per 2D workbench.
 */

import { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';
import { useCommandInteract } from '../../core/commands/commandInteract';

const DWELL_MS = 500;
const CURSOR_OFFSET = 16;

export function RolloverTip() {
    const rolloverOn = useSystemVarsStore((s) => s.ROLLOVERTIPS === 1);
    const hoveredId = useAppStore((s) => s.hoveredId);
    const entity = useAppStore((s) => (s.hoveredId ? s.entities[s.hoveredId] ?? null : null));
    const layer = useAppStore((s) => (entity ? s.layers[entity.layer] : undefined));
    // The balloon wins the cursor corner while it is open.
    const balloonOpen = useDynamicInputStore((s) => s.buffer.length > 0 || s.feedback !== null);
    const promptActive = useCommandInteract((s) => s.active);

    const [visible, setVisible] = useState(false);
    const clientRef = useRef({ x: 0, y: 0 });
    const tipRef = useRef<HTMLDivElement>(null);

    // Dwell timer: the tooltip appears after 500ms on the same entity and
    // hides the instant the hover moves on.
    useEffect(() => {
        setVisible(false);
        if (!rolloverOn || !hoveredId || balloonOpen || promptActive) return;
        const timer = window.setTimeout(() => setVisible(true), DWELL_MS);
        return () => window.clearTimeout(timer);
    }, [rolloverOn, hoveredId, balloonOpen, promptActive]);

    // Pointer tracking rides a ref; the tooltip position is written directly
    // (no per-move React re-render).
    useEffect(() => {
        const onMove = (e: PointerEvent) => {
            clientRef.current = { x: e.clientX, y: e.clientY };
            const el = tipRef.current;
            if (!el) return;
            let x = e.clientX + CURSOR_OFFSET;
            let y = e.clientY + CURSOR_OFFSET;
            if (x + el.offsetWidth > window.innerWidth - 8) x = e.clientX - el.offsetWidth - CURSOR_OFFSET;
            if (y + el.offsetHeight > window.innerHeight - 8) y = e.clientY - el.offsetHeight - CURSOR_OFFSET;
            el.style.transform = `translate(${x}px, ${y}px)`;
        };
        window.addEventListener('pointermove', onMove);
        return () => window.removeEventListener('pointermove', onMove);
    }, []);

    if (!rolloverOn || !visible || !entity || balloonOpen || promptActive) return null;

    const typeLabel = entity.type;
    const layerLabel = layer?.name ?? entity.layer;
    const nameLabel = entity.name;

    return (
        <Box
            ref={tipRef}
            position="fixed"
            top={0}
            left={0}
            zIndex={40}
            pointerEvents="none"
            bg="bg.panel"
            border="1px solid"
            borderColor="border.base"
            borderRadius="sm"
            px={2}
            py={1}
            shadow="md"
            maxW="240px"
            willChange="transform"
        >
            <Text fontSize="10px" color="text.primary" noOfLines={1} fontFamily="mono">
                {typeLabel}
                {nameLabel ? ` — ${nameLabel}` : ''}
            </Text>
            <Text fontSize="10px" color="text.secondary" noOfLines={1}>
                {layerLabel}
            </Text>
        </Box>
    );
}

export default RolloverTip;
